import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { BehaviorSubject } from 'rxjs';
import { concatMap, finalize, tap } from 'rxjs/operators';

import { Note, NoteEntityClass, NotesService } from '@assess/browser/api';
import { trackByFunctions } from '@assess/browser/track-by-functions';

import { dateFormatDefaultWith24HourTime } from '../../constants/date-formats';

export interface INoteEntityData {
  entityClass: NoteEntityClass;
  entityId: string;
  editDisabled: boolean;
  readDisabled: boolean;
  dragDisabled?: boolean;
  userId: string;
}
type mutableNote = Note & { isBeingEdited?: boolean; isConfirmingDelete?: boolean };
@Component({
  selector: 'assess-notes-dialog',
  templateUrl: './notes-dialog.component.html',
  styleUrls: ['./notes-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesDialogComponent implements OnInit {
  readonly defaultWith24HourTime = dateFormatDefaultWith24HourTime;
  emptyMessage = `Currently there are no notes.`;
  readonly trackById = trackByFunctions.byId;

  readonly editDisabled$$ = new BehaviorSubject<boolean>(false);
  readonly editDisabled$ = this.editDisabled$$.asObservable();

  readonly readDisabled$$ = new BehaviorSubject<boolean>(false);
  readonly readDisabled$ = this.readDisabled$$.asObservable();

  readonly dragDisabled$$ = new BehaviorSubject<boolean>(true);
  readonly dragDisabled$ = this.dragDisabled$$.asObservable();

  form!: FormGroup;

  readonly isLoading$$ = new BehaviorSubject<boolean>(true);
  readonly isLoading$ = this.isLoading$$.asObservable();

  notes!: mutableNote[];

  readonly isCreating$$ = new BehaviorSubject<boolean>(false);
  readonly isCreating$ = this.isCreating$$.asObservable();

  constructor(
    private readonly notesService: NotesService,
    @Inject(MAT_DIALOG_DATA)
    public data: INoteEntityData
  ) {}

  ngOnInit(): void {
    this.editDisabled$$.next(!!this.data.editDisabled);
    this.readDisabled$$.next(!!this.data.readDisabled);
    if (this.data.dragDisabled !== undefined) this.dragDisabled$$.next(!!this.data.dragDisabled);

    if (!this.readDisabled$$.value) {
      this.loadNotes().subscribe();
    } else {
      this.emptyMessage = 'Insufficient permissions';
    }
  }

  onNoteSaved() {
    this.isCreating$$.next(false);
    this.loadNotes().subscribe();
  }

  onDeleteNoteClick(note: Note) {
    const { entityClass, entityId } = this.data;
    this.notesService
      .deleteNote({ entityClass, entityId, noteId: note.id })
      .pipe(concatMap(() => this.loadNotes()))
      .subscribe();
  }

  onEditNoteClick(note: mutableNote) {
    note.isBeingEdited = true;
  }

  private loadNotes() {
    return this.notesService.getNotes({ entityClass: this.data.entityClass, entityId: this.data.entityId }).pipe(
      tap(() => this.isLoading$$.next(true)),
      tap((notes) => {
        this.notes = notes;
      }),
      finalize(() => {
        this.isLoading$$.next(false);
      })
    );
  }
}
